import {
  combineReducers,
  configureStore,
  createListenerMiddleware,
  type Reducer,
} from '@reduxjs/toolkit';

import { authReducer, loggedOut } from '@/shared/store/reducers/auth/auth.slice';
import {
  clearAuthStorage,
  readAuthFromStorage,
  writeAuthToStorage,
} from '@/shared/utils/auth-storage/auth-storage.util';
import { modalReducer } from '@/shared/store/reducers/modal/modal.slice';
import { preferencesReducer } from '@/shared/store/reducers/preferences/preferences.slice';
import {
  readPreferencesFromStorage,
  writePreferencesToStorage,
} from '@/shared/utils/preferences-storage/preferences-storage.util';
import { subscribeLookupInvalidation } from '@/shared/services/lookup-invalidation/lookup-invalidation';
import {
  getRegisteredApis,
  getRegisteredSubscribers,
} from '@/shared/store/registry/api-registry/api-registry';

/**
 * Builds the store from the slices the shell owns plus whatever apis have
 * registered themselves by import time (W4).
 *
 * A factory rather than a module-level singleton for two reasons: every spec
 * gets a clean store instead of inheriting the previous one's cache, and the
 * type back-edges in `app-store.types.ts` stay `import type` only.
 *
 * Persistence is a `store.subscribe` side effect, never a reducer write. That
 * keeps the reducers pure and — more usefully — puts the whole answer to "what
 * survives a reload?" in one place instead of scattering it across slices.
 * redux-persist is deliberately absent: it buys rehydration machinery for two
 * small keys that are read synchronously before React mounts anyway, at the cost
 * of an async hydration flash on every load.
 */
export function createAppStore() {
  const apis = getRegisteredApis();

  const rootReducer = combineReducers({
    auth: authReducer,
    preferences: preferencesReducer,
    // Deliberately NOT persisted: a reload must never restore a half-finished
    // dialog over a page whose data has since moved on.
    modal: modalReducer,
    ...Object.fromEntries(apis.map((api) => [api.reducerPath, api.reducer])),
  }) as Reducer;

  /**
   * Signing out empties every api cache.
   *
   * Without this, the next person to sign in at a shared workstation sees the
   * previous operator's rows painted from cache until each query refetches —
   * member names, verification cases, audit entries. RTK Query keys its cache by
   * endpoint and argument, not by actor, so it has no way to know the session
   * changed.
   *
   * Preferences deliberately survive: theme and language belong to the machine
   * and the person at it, not to the session.
   */
  const listeners = createListenerMiddleware();
  listeners.startListening({
    actionCreator: loggedOut,
    effect: (_action, api) => {
      clearAuthStorage();
      for (const registered of apis) {
        api.dispatch(registered.util.resetApiState());
      }
    },
  });

  const store = configureStore({
    reducer: rootReducer,
    preloadedState: {
      auth: readAuthFromStorage(),
      preferences: readPreferencesFromStorage(),
    },
    middleware: (getDefault) =>
      getDefault()
        .prepend(listeners.middleware)
        .concat(apis.map((api) => api.middleware)),
  });

  let lastAuth = store.getState().auth;
  let lastPreferences = store.getState().preferences;

  store.subscribe(() => {
    const { auth, preferences } = store.getState();

    if (auth !== lastAuth) {
      lastAuth = auth;
      writeAuthToStorage(auth);
    }

    if (preferences !== lastPreferences) {
      lastPreferences = preferences;
      writePreferencesToStorage(preferences);
    }
  });

  // The W6 lookup bridges hold their own cache of the same rows a module owns,
  // so a rename in the roles editor has to reach them through the bus (W5) —
  // wired here rather than from a component, so a stale entry cannot survive
  // until the next time a picker happens to open.
  subscribeLookupInvalidation(store.dispatch);

  // A module list refreshing after a write another slice made (P27c1): tags do
  // not cross slices, so the module subscribes to the typed event beside its
  // api and the store wires it, for the same reason the lookups are wired here.
  for (const subscribe of getRegisteredSubscribers()) {
    subscribe(store.dispatch);
  }

  return store;
}
